import React, { useCallback } from 'react';
import { Button, Space } from 'antd';
import type { FP620, FP621 } from '../../../services/pwp255Api';

interface Tab2DetailsProps {
  formData: Partial<FP620>;
  details: Partial<FP621>[];
  shippingUnit: number; // 1=YDS, 2=MTS, 3=PCS
  isEditing: boolean;
  onChange: (details: Partial<FP621>[]) => void;
  onFieldChange: (field: keyof FP620, value: string | number | null) => void;
}

const Tab2Details: React.FC<Tab2DetailsProps> = ({
  formData, details, shippingUnit, isEditing, onChange, onFieldChange,
}) => {
  const isPCS = shippingUnit === 3;
  const showMTS = !isPCS;

  const handleDetailChange = useCallback((index: number, field: keyof FP621, value: string | number) => {
    const next = details.map((d, i) => i === index ? { ...d, [field]: value } : d);
    onChange(next);
  }, [details, onChange]);

  // BR-009: Add detail row, inherit COLOR and 缸號 from previous row
  const handleAdd = useCallback(() => {
    if (details.length >= 999) return;
    const last = details[details.length - 1];
    const newRow: Partial<FP621> = {
      fp621_02: details.length + 1,
      fp621_11: '',
      fp621_12: last?.fp621_12 || '',
      fp621_121: last?.fp621_121 || '',
      fp621_13: last?.fp621_13 || '',
      fp621_14: 0,
      fp621_15: 0,
      fp621_16: 0,
      fp621_17: 0,
      fp621_18: 0,
      fp621_442: 0,
    };
    onChange([...details, newRow]);
  }, [details, onChange]);

  // BR-010: Delete detail row, reindex (T-F05)
  const handleDelete = useCallback((index: number) => {
    const next = details
      .filter((_, i) => i !== index)
      .map((d, i) => ({ ...d, fp621_02: i + 1 }));
    onChange(next);
  }, [details, onChange]);

  return (
    <div className="pwp255-tab2">
      {/* Header area: conversion rates (read-only display) */}
      <div className="pwp255-detail-header">
        <span className="pwp255-header-label">單位換算率</span>
        {showMTS && (
          <>
            <label>MTS</label>
            <input
              type="number"
              step="0.0001"
              value={formData.fp620_22 ?? 0.9144}
              readOnly
              disabled
              className="pwp255-input pwp255-readonly"
              style={{ width: 100 }}
            />
          </>
        )}
        <label>N.W.</label>
        <input
          type="number"
          step="0.0001"
          value={formData.fp620_23 ?? 0}
          readOnly
          disabled
          className="pwp255-input pwp255-readonly"
          style={{ width: 100 }}
        />
        <label>G.W.</label>
        <input
          type="number"
          step="0.0001"
          value={formData.fp620_24 ?? 0}
          readOnly
          disabled
          className="pwp255-input pwp255-readonly"
          style={{ width: 100 }}
        />
      </div>

      {/* Footer area: total items + add/delete buttons */}
      <div className="pwp255-detail-footer">
        <div className="pwp255-field-row">
          <label>總項數</label>
          <input
            type="number"
            value={formData.fp620_18 || 0}
            readOnly
            disabled
            className="pwp255-input pwp255-readonly"
            style={{ width: 80 }}
          />
        </div>
        {isEditing && (
          <Space>
            <Button
              size="small"
              type="primary"
              onClick={handleAdd}
              disabled={details.length >= 999}
            >
              明細新增
            </Button>
          </Space>
        )}
      </div>

      {/* Detail grid */}
      <div className="pwp255-detail-table-wrapper">
        <table className="pwp255-detail-table">
          <thead>
            <tr>
              <th style={{ width: 40 }}>項次</th>
              <th style={{ width: 60 }}>ROLL NO.</th>
              <th style={{ width: 220 }}>COLOR</th>
              <th style={{ width: 90 }}>缸號</th>
              <th style={{ width: 80 }}>{isPCS ? 'PCS' : 'YDS'}</th>
              {showMTS && <th style={{ width: 80 }}>MTS</th>}
              <th style={{ width: 75 }}>N.W.</th>
              <th style={{ width: 75 }}>G.W.</th>
              <th style={{ width: 60 }}>補布</th>
              {isEditing && <th style={{ width: 60 }}>操作</th>}
            </tr>
          </thead>
          <tbody>
            {details.length === 0 && (
              <tr>
                <td
                  colSpan={isEditing ? (showMTS ? 10 : 9) : (showMTS ? 9 : 8)}
                  style={{ textAlign: 'center', color: '#999' }}
                >
                  尚無明細資料
                </td>
              </tr>
            )}
            {details.map((d, idx) => (
              <tr key={idx}>
                {/* FP621_02 - 項次 (readOnly) */}
                <td>{d.fp621_02 ?? idx + 1}</td>

                {/* FP621_11 - ROLL NO. */}
                <td>
                  <input
                    type="text"
                    value={d.fp621_11 || ''}
                    readOnly={!isEditing}
                    onChange={e => handleDetailChange(idx, 'fp621_11', e.target.value)}
                    className="pwp255-detail-input"
                    style={{ width: 50 }}
                  />
                </td>

                {/* FP621_121 - COLOR/顏色代號(顯示) */}
                <td>
                  <input
                    type="text"
                    value={d.fp621_121 || ''}
                    readOnly={!isEditing}
                    onChange={e => handleDetailChange(idx, 'fp621_121', e.target.value)}
                    className="pwp255-detail-input"
                    style={{ width: 200 }}
                  />
                </td>

                {/* FP621_13 - 缸號 */}
                <td>
                  <input
                    type="text"
                    value={d.fp621_13 || ''}
                    readOnly={!isEditing}
                    onChange={e => handleDetailChange(idx, 'fp621_13', e.target.value)}
                    className="pwp255-detail-input"
                    style={{ width: 80 }}
                  />
                </td>

                {/* FP621_14 - YDS/PCS (number, triggers auto-calc) */}
                <td>
                  <input
                    type="number"
                    step="0.1"
                    value={d.fp621_14 ?? 0}
                    readOnly={!isEditing}
                    onChange={e => handleDetailChange(idx, 'fp621_14', parseFloat(e.target.value) || 0)}
                    className="pwp255-detail-input"
                    style={{ width: 70 }}
                  />
                </td>

                {/* FP621_17 - MTS (visible only when unit != 3) */}
                {showMTS && (
                  <td>
                    <input
                      type="number"
                      step="0.1"
                      value={d.fp621_17 ?? 0}
                      readOnly={!isEditing}
                      onChange={e => handleDetailChange(idx, 'fp621_17', parseFloat(e.target.value) || 0)}
                      className="pwp255-detail-input"
                      style={{ width: 70 }}
                    />
                  </td>
                )}

                {/* FP621_15 - N.W. */}
                <td>
                  <input
                    type="number"
                    step="0.1"
                    value={d.fp621_15 ?? 0}
                    readOnly={!isEditing}
                    onChange={e => handleDetailChange(idx, 'fp621_15', parseFloat(e.target.value) || 0)}
                    className="pwp255-detail-input"
                    style={{ width: 65 }}
                  />
                </td>

                {/* FP621_16 - G.W. (auto-calculated, readOnly) */}
                <td>
                  <input
                    type="number"
                    step="0.1"
                    value={d.fp621_16 ?? 0}
                    readOnly
                    disabled
                    className="pwp255-detail-input pwp255-readonly"
                    style={{ width: 65 }}
                  />
                </td>

                {/* FP621_18 - 補布 */}
                <td>
                  <input
                    type="number"
                    step="0.1"
                    value={d.fp621_18 ?? 0}
                    readOnly={!isEditing}
                    onChange={e => handleDetailChange(idx, 'fp621_18', parseFloat(e.target.value) || 0)}
                    className="pwp255-detail-input"
                    style={{ width: 50 }}
                  />
                </td>

                {/* Delete button */}
                {isEditing && (
                  <td>
                    <Button
                      size="small"
                      danger
                      onClick={() => handleDelete(idx)}
                    >
                      刪除
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default React.memo(Tab2Details);
